import { ApiResponse } from "../utils/apiUtils";
import { api, baseUrl } from "./apiSlice";

// ── Types ────────────────────────────────────────────────────────────────────

export interface IftaValidationSummaryItem {
  step: number;
  name: string;
  status: "ok" | "error";
}

export interface IftaValidationError {
  step: number;
  name: string;
  message: string;
}

export interface IftaValidationResult {
  valid: boolean;
  summary: IftaValidationSummaryItem[];
  errors: IftaValidationError[];
}

export interface IftaValidateRequest {
  file: File;
  quarter: string;
  hasTrucksNotHandled: boolean;
  hasMilesWithoutIvg: boolean;
  hasFuelWithoutCard: boolean;
}

export interface IftaGenerateRequest {
  file?: File | null;
  quarter: string;
  hasTrucksNotHandled: boolean;
  hasMilesWithoutIvg: boolean;
  hasFuelWithoutCard: boolean;
  /** Confirmed Stripe PaymentIntent id (pi_...) authorizing a paid generation. */
  paymentIntentId?: string | null;
}

export interface IftaTenantProfile {
  legalName?: string | null;
  dbaName?: string | null;
  iftaAccountNumber?: string | null;
  addressLine1?: string | null;
  cityStateZip?: string | null;
  phone?: string | null;
  isComplete: boolean;
  missingFields: string[];
}

export interface SaveIftaTenantProfileRequest {
  legalName?: string;
  dbaName?: string;
  iftaAccountNumber: string;
  addressLine1: string;
  cityStateZip: string;
  phone: string;
}

export interface IftaEligibility {
  planAllowed: boolean;
  isExempt: boolean;
  isFirstReport: boolean;
  requiresPayment: boolean;
  truckCount: number;
  billableTruckCount: number;
  subscribedTruckQuantity: number;
  unitAmount: number;
  amount: number;
  currency: string;
}

export interface IftaPaymentIntentResult {
  clientSecret: string;
  paymentIntentId: string;
  paymentId: number;
  truckCount: number;
  billableTruckCount: number;
  amount: number;
  currency: string;
  publishableKey: string;
}

/** Thrown by enqueueIftaReport when the server responds 402 Payment Required. */
export class IftaPaymentRequiredError extends Error {
  amount: number;
  currency: string;
  truckCount: number;
  billableTruckCount: number;
  constructor(amount: number, currency: string, truckCount = 0, billableTruckCount = 0) {
    super("Payment is required to generate this IFTA report.");
    this.name = "IftaPaymentRequiredError";
    this.amount = amount;
    this.currency = currency;
    this.truckCount = truckCount;
    this.billableTruckCount = billableTruckCount;
  }
}

/** Thrown when the tenant's plan does not include the IFTA feature (403). */
export class IftaPlanNotAllowedError extends Error {
  constructor(message?: string) {
    super(message || "The IFTA report feature is not available on your current plan.");
    this.name = "IftaPlanNotAllowedError";
  }
}

/** Builds the multipart form used by enqueue / eligibility / payment-intent. */
function buildIftaFormData(request: IftaGenerateRequest): FormData {
  const formData = new FormData();
  if (request.file) formData.append("File", request.file);
  formData.append("Quarter", request.quarter);
  formData.append("HasTrucksNotHandled", request.hasTrucksNotHandled.toString());
  formData.append("HasMilesWithoutIvg", request.hasMilesWithoutIvg.toString());
  formData.append("HasFuelWithoutCard", request.hasFuelWithoutCard.toString());
  if (request.paymentIntentId) formData.append("PaymentIntentId", request.paymentIntentId);
  return formData;
}

// ── RTK Query endpoints ──────────────────────────────────────────────────────

export const reportsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    validateIftaUpload: builder.mutation<
      IftaValidationResult,
      IftaValidateRequest
    >({
      query: ({ file, quarter, hasTrucksNotHandled, hasMilesWithoutIvg, hasFuelWithoutCard }) => {
        const formData = new FormData();
        formData.append("File", file);
        formData.append("Quarter", quarter);
        formData.append("HasTrucksNotHandled", hasTrucksNotHandled.toString());
        formData.append("HasMilesWithoutIvg", hasMilesWithoutIvg.toString());
        formData.append("HasFuelWithoutCard", hasFuelWithoutCard.toString());
        return {
          url: "reports/ifta/validate",
          method: "POST",
          body: formData,
        };
      },
      transformResponse: (response: ApiResponse<IftaValidationResult>) => response.data,
      transformErrorResponse: (response) => response,
    }),
    getIftaProfile: builder.query<IftaTenantProfile, void>({
      query: () => "reports/ifta/profile",
      transformResponse: (response: ApiResponse<IftaTenantProfile>) => response.data!,
      providesTags: ["IftaProfile"],
    }),
    saveIftaProfile: builder.mutation<IftaTenantProfile, SaveIftaTenantProfileRequest>({
      query: (body) => ({
        url: "reports/ifta/profile",
        method: "PUT",
        body,
      }),
      transformResponse: (response: ApiResponse<IftaTenantProfile>) => response.data!,
      invalidatesTags: ["IftaProfile"],
    }),
    getIftaSurcharges: builder.query<IftaTaxRateRow[], void>({
      query: () => "reports/ifta/surcharges",
      transformResponse: (response: ApiResponse<IftaTaxRateRow[]>) => response.data ?? [],
      providesTags: ["IftaSurcharges"],
    }),
    saveIftaSurcharges: builder.mutation<void, IftaTaxRateRow[]>({
      query: (rates) => ({
        url: "reports/ifta/surcharges",
        method: "PUT",
        body: { rates },
      }),
      invalidatesTags: ["IftaSurcharges"],
    }),
    getIftaGenerations: builder.query<IftaReportGeneration[], void>({
      query: () => "reports/ifta/generations",
      transformResponse: (response: ApiResponse<IftaReportGeneration[]>) => response.data ?? [],
      providesTags: ["IftaGenerations"],
    }),
  }),
});

export const {
  useValidateIftaUploadMutation,
  useGetIftaProfileQuery,
  useSaveIftaProfileMutation,
  useGetIftaSurchargesQuery,
  useSaveIftaSurchargesMutation,
  useGetIftaGenerationsQuery,
} = reportsApi;

// ── File download helpers (need auth token, so using fetch directly) ─────────
// RTK Query does not natively support blob/file responses, so these use fetch.

function triggerDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Downloads the IFTA data import template (GET api/reports/ifta/template).
 * Returns true on success, throws on error.
 */
export async function downloadIftaTemplate(token: string): Promise<void> {
  const response = await fetch(`${baseUrl}/reports/ifta/template`, {
    method: "GET",
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw new Error(`Failed to download template: ${response.status} ${response.statusText}`);
  }

  const blob = await response.blob();
  triggerDownload(blob, "IFTA-Data-Import.xlsx");
}

/**
 * Sends generate request and triggers browser download of the report.
 * On 501 (not yet implemented) or other errors, throws with a message.
 */
export async function generateAndDownloadIftaReport(
  request: IftaGenerateRequest,
  token: string
): Promise<void> {
  const formData = new FormData();
  if (request.file) formData.append("File", request.file);
  formData.append("Quarter", request.quarter);
  formData.append("HasTrucksNotHandled", request.hasTrucksNotHandled.toString());
  formData.append("HasMilesWithoutIvg", request.hasMilesWithoutIvg.toString());
  formData.append("HasFuelWithoutCard", request.hasFuelWithoutCard.toString());

  const response = await fetch(`${baseUrl}/reports/ifta/generate`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
      const body = await response.json();
      throw new Error(body?.message || `Error ${response.status}`);
    }
    throw new Error(`Failed to generate report: ${response.status} ${response.statusText}`);
  }

  const blob = await response.blob();
  triggerDownload(blob, `IFTA-Report-${request.quarter}.xlsx`);
}

export interface IftaEnqueueResult {
  jobId: string;
  quarter: string;
}

export interface IftaTaxRateRow {
  jurisdictionCode: string;
  rate: number;
}

export interface IftaReportGeneration {
  jobId: string;
  quarter: string;
  status: string;
  wasFree: boolean;
  createdAt: string;
  userId?: string | null;
  userName?: string | null;
}

export const IFTA_SURCHARGE_CODES = [
  { code: "SCHNY", label: "Surcharge NY" },
  { code: "SCHKY", label: "Surcharge KY" },
  { code: "SCHNM", label: "Surcharge NM" },
  { code: "SCHOR", label: "Surcharge OR" },
  { code: "SCHCT", label: "Surcharge CT" },
  { code: "SURCH_IN", label: "Surch. IN" },
  { code: "SURCH_KY", label: "Surch. KY" },
  { code: "SURCH_OH", label: "Surch. OH" },
  { code: "SURCH_VA", label: "Surch. VA" },
] as const;

/** sessionStorage key for persisting pending IFTA report job across navigations */
export const IFTA_JOB_SESSION_KEY = "ifta_pending_job";

/**
 * Enqueues an IFTA report generation job (POST /api/reports/ifta/enqueue).
 * Returns immediately with a jobId. The report is generated in the background.
 * Listen for the "IftaReportReady" SignalR event to know when it is ready.
 */
export async function enqueueIftaReport(
  request: IftaGenerateRequest,
  token: string
): Promise<IftaEnqueueResult> {
  const formData = buildIftaFormData(request);

  const response = await fetch(`${baseUrl}/reports/ifta/enqueue`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
      const body = await response.json();
      if (response.status === 402) {
        throw new IftaPaymentRequiredError(
          body?.data?.amount ?? 0,
          body?.data?.currency ?? "usd",
          body?.data?.truckCount ?? 0,
          body?.data?.billableTruckCount ?? 0,
        );
      }
      if (response.status === 403) {
        throw new IftaPlanNotAllowedError(body?.message);
      }
      throw new Error(body?.message || `Error ${response.status}`);
    }
    throw new Error(`Failed to enqueue report: ${response.status} ${response.statusText}`);
  }

  const body = await response.json();
  return body.data as IftaEnqueueResult;
}

/**
 * Gets IFTA billing eligibility for the current tenant based on the uploaded file
 * (POST api/reports/ifta/eligibility). The truck count is read from the file server-side.
 */
export async function getIftaEligibility(
  request: IftaGenerateRequest,
  token: string
): Promise<IftaEligibility> {
  const response = await fetch(`${baseUrl}/reports/ifta/eligibility`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: buildIftaFormData(request),
  });

  if (!response.ok) {
    throw new Error(`Failed to load eligibility: ${response.status} ${response.statusText}`);
  }

  const body = await response.json();
  return body.data as IftaEligibility;
}

/**
 * Creates a one-time Stripe PaymentIntent for an IFTA report (unit price * billable trucks).
 * The truck count is read from the uploaded file (POST api/reports/ifta/payment-intent).
 */
export async function createIftaPaymentIntent(
  request: IftaGenerateRequest,
  token: string
): Promise<IftaPaymentIntentResult> {
  const response = await fetch(`${baseUrl}/reports/ifta/payment-intent`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: buildIftaFormData(request),
  });

  if (!response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
      const body = await response.json();
      if (response.status === 403) {
        throw new IftaPlanNotAllowedError(body?.message);
      }
      throw new Error(body?.message || `Error ${response.status}`);
    }
    throw new Error(`Failed to create payment intent: ${response.status} ${response.statusText}`);
  }

  const body = await response.json();
  return body.data as IftaPaymentIntentResult;
}

/**
 * Downloads a generated IFTA report from blob storage and triggers browser download.
 */
export async function downloadIftaReport(
  jobId: string,
  quarter: string,
  token: string
): Promise<void> {
  const response = await fetch(
    `${baseUrl}/reports/ifta/download/${jobId}?quarter=${encodeURIComponent(quarter)}`,
    {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to download report: ${response.status} ${response.statusText}`);
  }

  const blob = await response.blob();
  triggerDownload(blob, `IFTA-Report-${quarter}.pdf`);
}

/**
 * Uploads quarterly IFTA tax rates (POST api/reports/ifta/tax-rates). IftaAdmin only.
 */
export async function uploadIftaTaxRates(
  file: File,
  quarter: string,
  token: string
): Promise<void> {
  const formData = new FormData();
  formData.append("File", file);
  formData.append("Quarter", quarter);

  const response = await fetch(`${baseUrl}/reports/ifta/tax-rates`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: formData,
  });

  if (!response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
      const body = await response.json();
      throw new Error(body?.message || `Error ${response.status}`);
    }
    throw new Error(`Failed to upload tax rates: ${response.status} ${response.statusText}`);
  }
}

